import type { ProductListItemInlineExternal } from 'types/capi/shared';

import InteractionEvent, {
  type InteractionTargetType
} from './InteractionEvent';
import type { ProductCardInteractionDisplayType } from './ProductCardInteractionEvent';

const NO_PRICE = -1;

export type InlineProductCardTargetProperties = {
  clickref?: string;
  destination_url: string;
  display_type?: ProductCardInteractionDisplayType;
  module_position?: number;
  module_type: string;
  position: number;
  price_currency: string;
  price_regular: number;
  price_sale?: number;
  product_name?: string;
  retailer_id?: string;
};

type EventData = {
  clickref?: string;
  displayType: ProductCardInteractionDisplayType;
  gesture?: string;
  guestId?: string;
  isBot: boolean;
  modulePosition?: number;
  moduleType: string;
  position: number;
  product: ProductListItemInlineExternal;
};

class InlineProductCardInteractionEvent extends InteractionEvent<
  EventData,
  InlineProductCardTargetProperties
> {
  protected get targetType(): InteractionTargetType {
    return 'inline_product_card';
  }

  protected getTargetProperties(): InlineProductCardTargetProperties {
    const {
      clickref,
      displayType,
      modulePosition,
      moduleType,
      position,
      product
    } = this.data;

    // The raw authored URL, not the clickref-tracked one the link navigates to: it is the key an
    // editor-typed product has instead of a catalogue id, and it is what the impression carries,
    // so a tracked URL here would join to nothing. The clickref rides beside it because every one
    // of these clicks is a direct click-out with no interstitial, so it is the spine to the partner
    // order, as on external_text_link; a card with no affiliate mints none, and omits it rather
    // than sending ''.
    return {
      clickref: clickref || undefined,
      destination_url: product.url,
      display_type: displayType,
      module_position: modulePosition,
      module_type: moduleType,
      position,
      price_currency: product.price.currency,
      price_regular: product.price.regular ?? NO_PRICE,
      price_sale: product.price.sale ?? undefined,
      product_name: product.name,
      retailer_id: product.affiliate?.canonical_id
    };
  }
}

export default InlineProductCardInteractionEvent;
